/**
 * Products API Client
 * CRUD operations for SKUs, Variants, and Categories
 */

import { getHttpClient } from '@spcc/core'
import type {
  SKU,
  SKUSummary,
  MediaAsset,
  Variant,
  VariantPortalAvailability,
  VariantSummary,
  Category,
  CategorySummary,
  CreateSKURequest,
  UpdateSKURequest,
  CreateVariantRequest,
  UpdateVariantRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../../domain/types'
import { PRODUCTS_API_ENDPOINTS } from '../../api/api-types'
import {
  transformSKUDto,
  transformSKUSummaryDto,
  transformVariantDto,
  transformVariantSummaryDto,
  transformCategoryDto,
  transformCategorySummaryDto,
  transformCreateSKURequest,
  transformUpdateSKURequest,
  transformCreateVariantRequest,
  transformUpdateVariantRequest,
  transformCreateCategoryRequest,
  transformUpdateCategoryRequest,
} from '../../api/transformers'
import type { SKUDto, VariantDto, CategoryDto } from '../../api/api-types'

// ============================================================================
// Helper Functions
// ============================================================================

function buildQueryString(params: Record<string, string | number | boolean>): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value))
  })
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

type ProductArEntry = { name?: string; description?: string }

/**
 * Fetch Arabic names/descriptions for a products endpoint.
 * Used to merge AR fields into EN list results since Strapi v5
 * does not reliably return `localizations` in list queries.
 */
async function fetchProductArNamesMap(
  http: ReturnType<typeof getHttpClient>,
  endpoint: string,
  extraFilters: Record<string, string | number | boolean> = {}
): Promise<Map<string, ProductArEntry>> {
  const map = new Map<string, ProductArEntry>()
  try {
    const qs = buildQueryString({
      locale: 'ar',
      'fields[0]': 'documentId',
      'fields[1]': 'name',
      'fields[2]': 'description',
      'pagination[pageSize]': '500',
      ...extraFilters,
    })
    const resp = await http.get<{ data: Array<{ documentId: string; name?: string; description?: string | null }> }>(
      `${endpoint}${qs}`
    )
    for (const item of resp.data.data) {
      if (item.documentId) {
        map.set(item.documentId, {
          name: item.name || undefined,
          description: item.description || undefined,
        })
      }
    }
  } catch { /* AR entries are optional */ }
  return map
}

function mergeArIntoProduct<T extends { documentId: string; name: { en: string; ar?: string } }>(
  item: T,
  arMap: Map<string, ProductArEntry>
): T {
  const ar = arMap.get(item.documentId)
  if (!ar?.name) return item
  return { ...item, name: { ...item.name, ar: ar.name } }
}

/** Strapi i18n: load linked locales so transformers can merge EN + AR for edit forms */
const STRAPI_LOCALIZATIONS_POPULATE: Record<string, string> = {
  'populate[localizations][fields][0]': 'locale',
  'populate[localizations][fields][1]': 'name',
  'populate[localizations][fields][2]': 'description',
}

const STRAPI_CATEGORY_RELATION_I18N: Record<string, string> = {
  'populate[categories][populate][localizations][fields][0]': 'locale',
  'populate[categories][populate][localizations][fields][1]': 'name',
  'populate[categories][populate][localizations][fields][2]': 'description',
}

const STRAPI_PARENT_RELATION_I18N: Record<string, string> = {
  'populate[parent][populate][localizations][fields][0]': 'locale',
  'populate[parent][populate][localizations][fields][1]': 'name',
  'populate[parent][populate][localizations][fields][2]': 'description',
}

const STRAPI_SKU_RELATION_I18N: Record<string, string> = {
  'populate[sku][populate][localizations][fields][0]': 'locale',
  'populate[sku][populate][localizations][fields][1]': 'name',
  'populate[sku][populate][localizations][fields][2]': 'description',
}

function getArabicLocalizedPayload(
  localized: { en: string; ar?: string },
  fieldName: string
): Record<string, string> {
  const arabicValue = localized.ar?.trim()
  if (!arabicValue) return {}
  return { [fieldName]: arabicValue }
}

function buildArabicNameDescriptionPayload(input: {
  name: { en: string; ar?: string }
  description?: { en: string; ar?: string }
}): Record<string, string> {
  return {
    ...getArabicLocalizedPayload(input.name, 'name'),
    ...(input.description ? getArabicLocalizedPayload(input.description, 'description') : {}),
  }
}

function toMediaAsset(value: unknown): MediaAsset | null {
  if (!value || typeof value !== 'object') return null
  const item = value as { id?: unknown; url?: unknown }
  if (
    (typeof item.id === 'string' || typeof item.id === 'number')
    && typeof item.url === 'string'
  ) {
    return { id: String(item.id), url: item.url }
  }
  return null
}

async function uploadImages(files: File[]): Promise<MediaAsset[]> {
  if (!files.length) return []

  const http = getHttpClient()
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })

  const uploadPaths = ['/api/upload']
  let lastError: unknown = null

  for (const path of uploadPaths) {
    try {
      const response = await http.post<unknown[]>(path, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data
        .map(toMediaAsset)
        .filter((item): item is MediaAsset => item !== null)
    } catch (error) {
      lastError = error
    }
  }

  throw lastError ?? new Error('Failed to upload images')
}

export const mediaApi = {
  uploadImages,
}

// ============================================================================
// SKU API
// ============================================================================

export const skuApi = {
  /**
   * Get all SKUs with categories and variant count
   */
  async getList(): Promise<SKU[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'filters[deletedAt][$null]': 'true',
      'populate[categories][fields][0]': 'documentId',
      'populate[categories][fields][1]': 'name',
      'populate[categories][fields][2]': 'code',
      'populate[categories][fields][3]': 'isActive',
      ...STRAPI_CATEGORY_RELATION_I18N,
      'populate[variants][fields][0]': 'documentId',
      'populate[variants][fields][1]': 'name',
      'populate[variants][fields][2]': 'variantCode',
      'populate[variants][fields][3]': 'weight',
      'populate[variants][fields][4]': 'packaging',
      'populate[variants][fields][5]': 'portalAvailability',
      'populate[variants][fields][6]': 'isActive',
      ...STRAPI_LOCALIZATIONS_POPULATE,
      'sort': 'name:asc',
      'pagination[pageSize]': '100',
    })

    const [response, arMap] = await Promise.all([
      http.get<{ data: SKUDto[] }>(`${PRODUCTS_API_ENDPOINTS.LIST_SKUS}${queryString}`),
      fetchProductArNamesMap(http, PRODUCTS_API_ENDPOINTS.LIST_SKUS, { 'filters[deletedAt][$null]': 'true' }),
    ])

    return response.data.data.map(transformSKUDto).map((s) => mergeArIntoProduct(s, arMap))
  },

  /**
   * Get SKU summaries (for dropdowns)
   */
  async getSummaries(): Promise<SKUSummary[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'filters[deletedAt][$null]': 'true',
      'filters[isActive][$eq]': 'true',
      ...STRAPI_LOCALIZATIONS_POPULATE,
      'sort': 'name:asc',
      'pagination[pageSize]': '100',
    })

    const [response, arMap] = await Promise.all([
      http.get<{ data: SKUDto[] }>(`${PRODUCTS_API_ENDPOINTS.LIST_SKUS}${queryString}`),
      fetchProductArNamesMap(http, PRODUCTS_API_ENDPOINTS.LIST_SKUS, {
        'filters[deletedAt][$null]': 'true',
        'filters[isActive][$eq]': 'true',
      }),
    ])

    return response.data.data.map(transformSKUSummaryDto).map((s) => mergeArIntoProduct(s, arMap))
  },

  /**
   * Get single SKU by documentId.
   * Fetches EN locale (with relations) and AR locale in parallel so Arabic
   * fields (name.ar, description.ar) are always populated for edit forms.
   */
  async getById(documentId: string): Promise<SKU | null> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'populate[categories][fields][0]': 'documentId',
      'populate[categories][fields][1]': 'name',
      'populate[categories][fields][2]': 'code',
      'populate[categories][fields][3]': 'isActive',
      ...STRAPI_CATEGORY_RELATION_I18N,
      'populate[variants][fields][0]': 'documentId',
      'populate[variants][fields][1]': 'name',
      'populate[variants][fields][2]': 'variantCode',
      'populate[variants][fields][3]': 'barcode',
      'populate[variants][fields][4]': 'weight',
      'populate[variants][fields][5]': 'packaging',
      'populate[variants][fields][6]': 'portalAvailability',
      'populate[variants][fields][7]': 'isActive',
      ...STRAPI_LOCALIZATIONS_POPULATE,
    })
    const arQueryString = buildQueryString({
      locale: 'ar',
      'fields[0]': 'name',
      'fields[1]': 'description',
    })

    try {
      const [enResult, arResult] = await Promise.allSettled([
        http.get<{ data: SKUDto }>(`${PRODUCTS_API_ENDPOINTS.GET_SKU(documentId)}${queryString}`),
        http.get<{ data: { name?: string; description?: string | null } }>(
          `${PRODUCTS_API_ENDPOINTS.GET_SKU(documentId)}${arQueryString}`
        ),
      ])

      if (enResult.status === 'rejected') return null

      const sku = transformSKUDto(enResult.value.data.data)

      if (arResult.status === 'fulfilled') {
        const arDto = arResult.value.data.data
        if (arDto?.name?.trim()) {
          sku.name = { ...sku.name, ar: arDto.name }
        }
        if (arDto?.description?.trim()) {
          sku.description = { ...(sku.description ?? { en: '' }), ar: arDto.description }
        }
      }

      return sku
    } catch {
      return null
    }
  },

  /**
   * Create new SKU
   */
  async create(request: CreateSKURequest): Promise<SKU> {
    const http = getHttpClient()
    const response = await http.post<{ data: SKUDto }>(`${PRODUCTS_API_ENDPOINTS.CREATE_SKU}?locale=en`, {
      data: transformCreateSKURequest(request),
    })
    const documentId = response.data.data.documentId

    const localizedData = buildArabicNameDescriptionPayload({
      name: request.name,
      description: request.description,
    })
    if (Object.keys(localizedData).length > 0) {
      await http.put<{ data: SKUDto }>(
        `${PRODUCTS_API_ENDPOINTS.UPDATE_SKU(documentId)}?locale=ar`,
        { data: localizedData }
      )
    }
    const created = await skuApi.getById(documentId)
    return created!
  },

  /**
   * Update existing SKU
   */
  async update(request: UpdateSKURequest): Promise<SKU> {
    const http = getHttpClient()
    await http.put<{ data: SKUDto }>(
      `${PRODUCTS_API_ENDPOINTS.UPDATE_SKU(request.documentId)}?locale=en`,
      {
        data: transformUpdateSKURequest(request),
      }
    )

    const localizedData = buildArabicNameDescriptionPayload({
      name: request.name,
      description: request.description,
    })
    if (Object.keys(localizedData).length > 0) {
      await http.put<{ data: SKUDto }>(
        `${PRODUCTS_API_ENDPOINTS.UPDATE_SKU(request.documentId)}?locale=ar`,
        { data: localizedData }
      )
    }

    const updated = await skuApi.getById(request.documentId)
    return updated!
  },

  /**
   * Soft delete SKU
   */
  async delete(documentId: string): Promise<boolean> {
    const http = getHttpClient()
    try {
      await http.put(PRODUCTS_API_ENDPOINTS.UPDATE_SKU(documentId), {
        data: { deletedAt: new Date().toISOString() },
      })
      return true
    } catch {
      return false
    }
  },

}

// ============================================================================
// Variant API
// ============================================================================

export const variantApi = {
  /**
   * Get all variants with SKU info
   */
  async getList(): Promise<Variant[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'filters[deletedAt][$null]': 'true',
      'populate[images][fields][0]': 'url',
      'populate[sku][fields][0]': 'documentId',
      'populate[sku][fields][1]': 'name',
      'populate[sku][fields][2]': 'isActive',
      ...STRAPI_SKU_RELATION_I18N,
      ...STRAPI_LOCALIZATIONS_POPULATE,
      'sort': 'variantCode:asc',
      'pagination[pageSize]': '200',
    })

    const [response, arMap] = await Promise.all([
      http.get<{ data: VariantDto[] }>(`${PRODUCTS_API_ENDPOINTS.LIST_VARIANTS}${queryString}`),
      fetchProductArNamesMap(http, PRODUCTS_API_ENDPOINTS.LIST_VARIANTS, { 'filters[deletedAt][$null]': 'true' }),
    ])

    return response.data.data.map(transformVariantDto).map((v) => mergeArIntoProduct(v, arMap))
  },

  /**
   * Get variant summaries for a specific SKU
   */
  async getBySkuId(skuDocumentId: string): Promise<VariantSummary[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'filters[sku][documentId][$eq]': skuDocumentId,
      'filters[deletedAt][$null]': 'true',
      'populate[images][fields][0]': 'url',
      ...STRAPI_LOCALIZATIONS_POPULATE,
      'sort': 'variantCode:asc',
      'pagination[pageSize]': '100',
    })

    const [response, arMap] = await Promise.all([
      http.get<{ data: VariantDto[] }>(`${PRODUCTS_API_ENDPOINTS.LIST_VARIANTS}${queryString}`),
      fetchProductArNamesMap(http, PRODUCTS_API_ENDPOINTS.LIST_VARIANTS, {
        'filters[sku][documentId][$eq]': skuDocumentId,
        'filters[deletedAt][$null]': 'true',
      }),
    ])

    return response.data.data.map(transformVariantSummaryDto).map((v) => mergeArIntoProduct(v, arMap))
  },

  /**
   * Get single variant by documentId.
   * Fetches EN locale (with relations) and AR locale in parallel so Arabic
   * fields (name.ar, description.ar) are always populated for edit forms.
   */
  async getById(documentId: string): Promise<Variant | null> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'populate[images][fields][0]': 'url',
      'populate[sku][fields][0]': 'documentId',
      'populate[sku][fields][1]': 'name',
      'populate[sku][fields][2]': 'isActive',
      ...STRAPI_SKU_RELATION_I18N,
      ...STRAPI_LOCALIZATIONS_POPULATE,
    })
    const arQueryString = buildQueryString({
      locale: 'ar',
      'fields[0]': 'name',
      'fields[1]': 'description',
    })

    try {
      const [enResult, arResult] = await Promise.allSettled([
        http.get<{ data: VariantDto }>(
          `${PRODUCTS_API_ENDPOINTS.GET_VARIANT(documentId)}${queryString}`
        ),
        http.get<{ data: { name?: string; description?: string | null } }>(
          `${PRODUCTS_API_ENDPOINTS.GET_VARIANT(documentId)}${arQueryString}`
        ),
      ])

      if (enResult.status === 'rejected') return null

      const variant = transformVariantDto(enResult.value.data.data)

      if (arResult.status === 'fulfilled') {
        const arDto = arResult.value.data.data
        if (arDto?.name?.trim()) {
          variant.name = { ...variant.name, ar: arDto.name }
        }
        if (arDto?.description?.trim()) {
          variant.description = { ...(variant.description ?? { en: '' }), ar: arDto.description }
        }
      }

      return variant
    } catch {
      return null
    }
  },

  /**
   * Create new variant
   */
  async create(request: CreateVariantRequest): Promise<Variant> {
    const http = getHttpClient()
    const response = await http.post<{ data: VariantDto }>(`${PRODUCTS_API_ENDPOINTS.CREATE_VARIANT}?locale=en`, {
      data: transformCreateVariantRequest(request),
    })
    const documentId = response.data.data.documentId

    const localizedData = buildArabicNameDescriptionPayload({
      name: request.name,
      description: request.description,
    })
    if (Object.keys(localizedData).length > 0) {
      await http.put<{ data: VariantDto }>(
        `${PRODUCTS_API_ENDPOINTS.UPDATE_VARIANT(documentId)}?locale=ar`,
        { data: localizedData }
      )
    }

    const created = await variantApi.getById(documentId)
    return created!
  },

  /**
   * Update existing variant
   */
  async update(request: UpdateVariantRequest): Promise<Variant> {
    const http = getHttpClient()
    await http.put<{ data: VariantDto }>(
      `${PRODUCTS_API_ENDPOINTS.UPDATE_VARIANT(request.documentId)}?locale=en`,
      {
        data: transformUpdateVariantRequest(request),
      }
    )

    const localizedData = buildArabicNameDescriptionPayload({
      name: request.name,
      description: request.description,
    })
    if (Object.keys(localizedData).length > 0) {
      await http.put<{ data: VariantDto }>(
        `${PRODUCTS_API_ENDPOINTS.UPDATE_VARIANT(request.documentId)}?locale=ar`,
        { data: localizedData }
      )
    }

    const updated = await variantApi.getById(request.documentId)
    return updated!
  },

  /**
   * Update portal availability only
   */
  async updatePortalAvailability(
    documentId: string,
    portalAvailability: VariantPortalAvailability
  ): Promise<VariantPortalAvailability> {
    const http = getHttpClient()
    await http.put<{ data: VariantDto }>(
      PRODUCTS_API_ENDPOINTS.UPDATE_VARIANT(documentId),
      {
        data: { portalAvailability },
      }
    )
    return portalAvailability
  },

  /**
   * Soft delete variant
   */
  async delete(documentId: string): Promise<boolean> {
    const http = getHttpClient()
    try {
      await http.put(PRODUCTS_API_ENDPOINTS.UPDATE_VARIANT(documentId), {
        data: { deletedAt: new Date().toISOString() },
      })
      return true
    } catch {
      return false
    }
  },
}

// ============================================================================
// Category API
// ============================================================================

export const categoryApi = {
  /**
   * Get all categories
   */
  async getList(): Promise<Category[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'filters[deletedAt][$null]': 'true',
      'populate[parent][fields][0]': 'documentId',
      'populate[parent][fields][1]': 'name',
      'populate[parent][fields][2]': 'code',
      ...STRAPI_PARENT_RELATION_I18N,
      ...STRAPI_LOCALIZATIONS_POPULATE,
      'sort': 'name:asc',
      'pagination[pageSize]': '100',
    })

    const [response, arMap] = await Promise.all([
      http.get<{ data: CategoryDto[] }>(`${PRODUCTS_API_ENDPOINTS.LIST_CATEGORIES}${queryString}`),
      fetchProductArNamesMap(http, PRODUCTS_API_ENDPOINTS.LIST_CATEGORIES, { 'filters[deletedAt][$null]': 'true' }),
    ])

    return response.data.data.map(transformCategoryDto).map((c) => mergeArIntoProduct(c, arMap))
  },

  /**
   * Get active category summaries (for dropdowns)
   */
  async getSummaries(): Promise<CategorySummary[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'filters[deletedAt][$null]': 'true',
      'filters[isActive][$eq]': 'true',
      ...STRAPI_LOCALIZATIONS_POPULATE,
      'sort': 'name:asc',
      'pagination[pageSize]': '100',
    })

    const [response, arMap] = await Promise.all([
      http.get<{ data: CategoryDto[] }>(`${PRODUCTS_API_ENDPOINTS.LIST_CATEGORIES}${queryString}`),
      fetchProductArNamesMap(http, PRODUCTS_API_ENDPOINTS.LIST_CATEGORIES, {
        'filters[deletedAt][$null]': 'true',
        'filters[isActive][$eq]': 'true',
      }),
    ])

    return response.data.data.map(transformCategorySummaryDto).map((c) => mergeArIntoProduct(c, arMap))
  },

  /**
   * Get single category by documentId.
   * Fetches EN locale (with relations) and AR locale in parallel so Arabic
   * fields (name.ar, description.ar) are always populated for edit forms.
   */
  async getById(documentId: string): Promise<Category | null> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      locale: 'en',
      'populate[parent][fields][0]': 'documentId',
      'populate[parent][fields][1]': 'name',
      'populate[parent][fields][2]': 'code',
      ...STRAPI_PARENT_RELATION_I18N,
      ...STRAPI_LOCALIZATIONS_POPULATE,
    })
    const arQueryString = buildQueryString({
      locale: 'ar',
      'fields[0]': 'name',
      'fields[1]': 'description',
    })

    try {
      const [enResult, arResult] = await Promise.allSettled([
        http.get<{ data: CategoryDto }>(
          `${PRODUCTS_API_ENDPOINTS.GET_CATEGORY(documentId)}${queryString}`
        ),
        http.get<{ data: { name?: string; description?: string | null } }>(
          `${PRODUCTS_API_ENDPOINTS.GET_CATEGORY(documentId)}${arQueryString}`
        ),
      ])

      if (enResult.status === 'rejected') return null

      const category = transformCategoryDto(enResult.value.data.data)

      if (arResult.status === 'fulfilled') {
        const arDto = arResult.value.data.data
        if (arDto?.name?.trim()) {
          category.name = { ...category.name, ar: arDto.name }
        }
        if (arDto?.description?.trim()) {
          category.description = { ...(category.description ?? { en: '' }), ar: arDto.description }
        }
      }

      return category
    } catch {
      return null
    }
  },

  /**
   * Create new category
   */
  async create(request: CreateCategoryRequest): Promise<Category> {
    const http = getHttpClient()
    const response = await http.post<{ data: CategoryDto }>(
      `${PRODUCTS_API_ENDPOINTS.CREATE_CATEGORY}?locale=en`,
      { data: transformCreateCategoryRequest(request) }
    )
    const documentId = response.data.data.documentId

    const localizedData = buildArabicNameDescriptionPayload({
      name: request.name,
      description: request.description,
    })
    if (Object.keys(localizedData).length > 0) {
      await http.put<{ data: CategoryDto }>(
        `${PRODUCTS_API_ENDPOINTS.UPDATE_CATEGORY(documentId)}?locale=ar`,
        { data: localizedData }
      )
    }

    const created = await categoryApi.getById(documentId)
    return created!
  },

  /**
   * Update existing category
   */
  async update(request: UpdateCategoryRequest): Promise<Category> {
    const http = getHttpClient()
    await http.put<{ data: CategoryDto }>(
      `${PRODUCTS_API_ENDPOINTS.UPDATE_CATEGORY(request.documentId)}?locale=en`,
      { data: transformUpdateCategoryRequest(request) }
    )

    const localizedData = buildArabicNameDescriptionPayload({
      name: request.name,
      description: request.description,
    })
    if (Object.keys(localizedData).length > 0) {
      await http.put<{ data: CategoryDto }>(
        `${PRODUCTS_API_ENDPOINTS.UPDATE_CATEGORY(request.documentId)}?locale=ar`,
        { data: localizedData }
      )
    }

    const updated = await categoryApi.getById(request.documentId)
    return updated!
  },
}

// ============================================================================
// Combined Products API
// ============================================================================

export const productsApi = {
  skus: skuApi,
  variants: variantApi,
  categories: categoryApi,

  /**
   * Get all data for products admin section
   */
  async getProductsData(): Promise<{
    skus: SKU[]
    variants: Variant[]
    categories: Category[]
  }> {
    const [skus, variants, categories] = await Promise.all([
      skuApi.getList(),
      variantApi.getList(),
      categoryApi.getList(),
    ])

    return { skus, variants, categories }
  },

  /**
   * Fetch the full audit timeline for a variant.
   */
  async getVariantTimeline(variantDocumentId: string): Promise<import('../../domain/types/variant-timeline.types').VariantEvent[]> {
    const http = getHttpClient()
    try {
      const resp = await http.get<{ data: import('../../domain/types/variant-timeline.types').VariantEvent[] }>(
        `/api/variants/${variantDocumentId}/timeline`
      )
      return resp.data.data ?? []
    } catch {
      return []
    }
  },
}
